import { Link } from '@tanstack/react-router';
import {
  ArrowRight,
  BarChart3,
  Building2,
  CalendarCog,
  Check,
  ClipboardCheck,
  Flag,
  Globe,
  LayoutDashboard,
  LogIn,
  Mail,
  MonitorPlay,
  ShieldCheck,
  Smartphone,
  Trophy,
  Users,
  WifiOff,
  Zap,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useDocumentTitle } from '../components/AppShell';
import { LogoMark, Wordmark } from '../components/brand/Logo';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { buttonClass } from '../components/ui';
import { getSession } from '../lib/auth-store';
import { isEmbeddedApp } from '../lib/embedded';
import { CREATE_PERSONAL_PATH } from '../features/account/common/paths';
import {
  CaddyPhonePreview,
  ClubPreview,
  ConsolePreview,
  LeaderboardPreview,
  ScorecardStrip,
  TvPreview,
} from './landing/previews';
import './landing/landing.css';

/** Sales contact for the "Contact us" buttons; set VITE_CONTACT_EMAIL per deployment. */
const CONTACT_EMAIL =
  (import.meta.env.VITE_CONTACT_EMAIL as string | undefined) || 'contact@prymerlab.com';

const LANGS = [
  { code: 'vi', name: 'Tiếng Việt', sample: 'Bảng xếp hạng trực tiếp' },
  { code: 'en', name: 'English', sample: 'Live leaderboard' },
  { code: 'ko', name: '한국어', sample: '실시간 리더보드' },
  { code: 'zh', name: '简体中文', sample: '实时排行榜' },
  { code: 'ja', name: '日本語', sample: 'ライブリーダーボード' },
] as const;

function Bullets({ items }: { items: string[] }) {
  return (
    <ul className="lp-bullets">
      {items.map((b) => (
        <li key={b}>
          <span className="lp-bullets__tick" aria-hidden="true">
            <Check size={14} strokeWidth={3} />
          </span>
          {b}
        </li>
      ))}
    </ul>
  );
}

function Feature({
  id,
  icon,
  k,
  visual,
  flip,
  tone,
  extra,
}: {
  id: string;
  icon: ReactNode;
  k: string;
  visual: ReactNode;
  flip?: boolean;
  tone?: 'night';
  extra?: ReactNode;
}) {
  const { t } = useTranslation('auth');
  const bullets = t(`landing.features.${k}.bullets`, { returnObjects: true }) as unknown;
  return (
    <section
      id={id}
      className={`lp-feature${flip ? ' lp-feature--flip' : ''}${tone ? ` lp-feature--${tone}` : ''}`}
      aria-labelledby={`${id}-title`}
    >
      <div className="lp-wrap lp-feature__grid">
        <div className="lp-feature__copy">
          <p className="lp-eyebrow">
            <span className="lp-eyebrow__icon" aria-hidden="true">
              {icon}
            </span>
            {t(`landing.features.${k}.eyebrow`)}
          </p>
          <h2 id={`${id}-title`}>{t(`landing.features.${k}.title`)}</h2>
          <p className="lp-lead">{t(`landing.features.${k}.body`)}</p>
          {Array.isArray(bullets) ? <Bullets items={bullets as string[]} /> : null}
          {extra}
        </div>
        <div className="lp-feature__visual">{visual}</div>
      </div>
    </section>
  );
}

/**
 * Secondary call to action for individual golfers: "create a tournament with friends" (나의 대회,
 * §15 #405). Signed out it goes straight to sign-up (which links to login), returning to
 * /me/tournaments/new, which makes the personal space and opens the wizard.
 */
function CreateOwnLink({ signedIn, testId }: { signedIn: boolean; testId: string }) {
  const { t } = useTranslation('auth');
  const className = `${buttonClass({ size: 'lg' })} lp-btn-ghost-dark`;
  const body = (
    <>
      <Trophy size={20} aria-hidden="true" />
      {t('landing.cta.createOwn')}
    </>
  );
  return signedIn ? (
    <Link to={CREATE_PERSONAL_PATH} className={className} data-testid={testId}>
      {body}
    </Link>
  ) : (
    <Link
      to="/register"
      search={{ redirect: CREATE_PERSONAL_PATH } as never}
      className={className}
      data-testid={testId}
    >
      {body}
    </Link>
  );
}

/** Public product site at `/`: the front door for golf courses, associations and clubs. */
export function HomePage() {
  const { t } = useTranslation('auth');
  useDocumentTitle(t('landing.docTitle'));
  const signedIn = getSession('USER') !== null;
  // inside the PinGo app (§15 #402) no prices: store payment policies (§15 #427)
  const showPricing = !isEmbeddedApp();
  const mailto = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(t('landing.cta.mailSubject'))}`;
  const year = new Date().getFullYear();
  const list = (key: string) => {
    const v = t(key, { returnObjects: true }) as unknown;
    return Array.isArray(v) ? (v as string[]) : [];
  };

  return (
    <div className="lp">
      <a className="lp-skip" href="#lp-main">
        {t('landing.skip')}
      </a>
      <header className="lp-nav">
        <div className="lp-wrap lp-nav__inner">
          <Link to="/" className="lp-nav__brand" aria-label={t('landing.homeLabel')}>
            <LogoMark size={32} ring />
            <span aria-hidden="true" className="lp-nav__wordmark">
              <Wordmark onDark />
            </span>
          </Link>
          <nav className="lp-nav__links" aria-label={t('landing.nav.label')}>
            <a href="#features">{t('landing.nav.features')}</a>
            <a href="#how">{t('landing.nav.how')}</a>
            {showPricing && <a href="#pricing">{t('landing.nav.pricing')}</a>}
            <Link to="/me/tournaments">{t('landing.nav.clubs')}</Link>
          </nav>
          <div className="lp-nav__actions">
            <LanguageSwitcher compact />
            {signedIn ? (
              <Link to="/admin" className={buttonClass({ variant: 'accent' })}>
                <LayoutDashboard size={18} aria-hidden="true" />
                <span>{t('landing.nav.console')}</span>
              </Link>
            ) : (
              <Link to="/login" className={`${buttonClass()} lp-nav__login`}>
                <LogIn size={18} aria-hidden="true" />
                <span>{t('landing.nav.login')}</span>
              </Link>
            )}
          </div>
        </div>
      </header>

      <main id="lp-main">
        {/* ------------------------------------------------------------ hero */}
        <section className="lp-hero" aria-labelledby="lp-hero-title">
          <div className="lp-wrap lp-hero__grid">
            <div className="lp-hero__copy">
              <p className="lp-hero__eyebrow">
                <span className="lp-live-dot" aria-hidden="true" />
                {t('landing.hero.eyebrow')}
              </p>
              <h1 id="lp-hero-title">
                {t('landing.hero.title')}
                <span className="lp-hero__accent">{t('landing.hero.titleAccent')}</span>
              </h1>
              <p className="lp-hero__lead">{t('landing.hero.lead')}</p>
              <div className="lp-hero__ctas">
                <a href={mailto} className={buttonClass({ variant: 'accent', size: 'lg' })}>
                  <Mail size={20} aria-hidden="true" />
                  {t('landing.cta.contact')}
                </a>
                <CreateOwnLink signedIn={signedIn} testId="lp-create-hero" />
              </div>
              <ul className="lp-hero__points">
                {list('landing.hero.points').map((p) => (
                  <li key={p}>
                    <ShieldCheck size={16} aria-hidden="true" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
            <div className="lp-hero__visual">
              <LeaderboardPreview compact />
              <CaddyPhonePreview players={2} />
            </div>
          </div>
          <div className="lp-wrap">
            <dl className="lp-stats">
              {(['players', 'caddies', 'viewers', 'languages'] as const).map((k) => (
                <div key={k} className="lp-stats__item">
                  <dt>{t(`landing.stats.${k}.label`)}</dt>
                  <dd className="num">{t(`landing.stats.${k}.value`)}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>

        {/* ------------------------------------------------------------ pillars */}
        <section className="lp-pillars" aria-labelledby="lp-pillars-title">
          <div className="lp-wrap">
            <div className="lp-section-head">
              <p className="lp-eyebrow">{t('landing.pillars.eyebrow')}</p>
              <h2 id="lp-pillars-title">{t('landing.pillars.title')}</h2>
            </div>
            <div className="lp-pillars__grid">
              {(
                [
                  ['verified', <ShieldCheck key="i" size={22} />],
                  ['instant', <Zap key="i" size={22} />],
                  ['live', <MonitorPlay key="i" size={22} />],
                ] as const
              ).map(([k, icon]) => (
                <article key={k} className="lp-pillar">
                  <span className="lp-pillar__icon" aria-hidden="true">
                    {icon}
                  </span>
                  <h3>{t(`landing.pillars.${k}.title`)}</h3>
                  <p>{t(`landing.pillars.${k}.body`)}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------------ features */}
        <div id="features">
          <Feature
            id="lp-live"
            k="live"
            icon={<BarChart3 size={16} />}
            visual={
              <div className="lp-stack">
                <TvPreview />
                <ScorecardStrip />
              </div>
            }
          />
          <Feature
            id="lp-caddy"
            k="caddy"
            flip
            tone="night"
            icon={<WifiOff size={16} />}
            visual={
              <div className="lp-phone-solo">
                <CaddyPhonePreview offline />
              </div>
            }
          />
          <Feature
            id="lp-console"
            k="console"
            icon={<LayoutDashboard size={16} />}
            visual={<ConsolePreview />}
            extra={
              <Link
                to="/admin"
                className={`${buttonClass({ variant: 'primary' })} lp-feature__link`}
              >
                {t('landing.features.console.open')}
                <ArrowRight size={18} aria-hidden="true" />
              </Link>
            }
          />
          <Feature
            id="lp-clubs"
            k="clubs"
            flip
            icon={<Users size={16} />}
            visual={<ClubPreview />}
            extra={
              <Link to="/me/tournaments" className={`${buttonClass()} lp-feature__link`}>
                {t('landing.features.clubs.open')}
                <ArrowRight size={18} aria-hidden="true" />
              </Link>
            }
          />
          <Feature
            id="lp-langs"
            k="langs"
            icon={<Globe size={16} />}
            visual={
              <ul className="lp-langs" aria-label={t('landing.features.langs.eyebrow')}>
                {LANGS.map((l) => (
                  <li key={l.code} lang={l.code} className="lp-langs__item">
                    <span className="lp-langs__code">{l.code.toUpperCase()}</span>
                    <span className="lp-langs__name">{l.name}</span>
                    <strong className="lp-langs__sample">{l.sample}</strong>
                  </li>
                ))}
              </ul>
            }
          />
        </div>

        {/* ------------------------------------------------------------ how it works */}
        <section id="how" className="lp-how" aria-labelledby="lp-how-title">
          <div className="lp-wrap">
            <div className="lp-section-head">
              <p className="lp-eyebrow">{t('landing.how.eyebrow')}</p>
              <h2 id="lp-how-title">{t('landing.how.title')}</h2>
            </div>
            <ol className="lp-how__steps">
              {(
                [
                  ['setup', <CalendarCog key="i" size={22} />],
                  ['pair', <Users key="i" size={22} />],
                  ['play', <Smartphone key="i" size={22} />],
                  ['final', <ClipboardCheck key="i" size={22} />],
                ] as const
              ).map(([k, icon], i) => (
                <li key={k} className="lp-step">
                  <div className="lp-step__top">
                    <span className="lp-step__num num">{String(i + 1).padStart(2, '0')}</span>
                    <span className="lp-step__icon" aria-hidden="true">
                      {icon}
                    </span>
                  </div>
                  <h3>{t(`landing.how.${k}.title`)}</h3>
                  <p>{t(`landing.how.${k}.body`)}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* ------------------------------------------------------------ audiences */}
        <section className="lp-audience" aria-labelledby="lp-audience-title">
          <div className="lp-wrap">
            <div className="lp-section-head">
              <p className="lp-eyebrow">{t('landing.audience.eyebrow')}</p>
              <h2 id="lp-audience-title">{t('landing.audience.title')}</h2>
            </div>
            <div className="lp-audience__grid">
              {(
                [
                  ['courses', <Flag key="i" size={22} />],
                  ['associations', <Building2 key="i" size={22} />],
                  ['clubs', <Trophy key="i" size={22} />],
                ] as const
              ).map(([k, icon]) => (
                <article key={k} className="lp-audience__card">
                  <span className="lp-pillar__icon" aria-hidden="true">
                    {icon}
                  </span>
                  <h3>{t(`landing.audience.${k}.title`)}</h3>
                  <p>{t(`landing.audience.${k}.body`)}</p>
                  <Bullets items={list(`landing.audience.${k}.bullets`)} />
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------------ pricing */}
        {showPricing && (
          <section id="pricing" className="lp-pricing" aria-labelledby="lp-pricing-title">
            <div className="lp-wrap lp-pricing__grid">
              <div>
                <p className="lp-eyebrow">{t('landing.pricing.eyebrow')}</p>
                <h2 id="lp-pricing-title">{t('landing.pricing.title')}</h2>
                <p className="lp-lead">{t('landing.pricing.body')}</p>
                <Bullets items={list('landing.pricing.included')} />
              </div>
              <div className="lp-price">
                <p className="lp-price__label">{t('landing.pricing.planLabel')}</p>
                <div className="lp-price__tier">
                  <span>{t('landing.pricing.tier1')}</span>
                  <strong>
                    <b className="num">80,000</b> {t('landing.pricing.unit')}
                  </strong>
                </div>
                <div className="lp-price__tier lp-price__tier--best">
                  <span>{t('landing.pricing.tier2')}</span>
                  <strong>
                    <b className="num">60,000</b> {t('landing.pricing.unit')}
                  </strong>
                </div>
                <p className="lp-price__note">{t('landing.pricing.note')}</p>
                <a href={mailto} className={buttonClass({ variant: 'accent', block: true })}>
                  {t('landing.pricing.cta')}
                </a>
              </div>
            </div>
          </section>
        )}

        {/* ------------------------------------------------------------ final CTA */}
        <section className="lp-cta" aria-labelledby="lp-cta-title">
          <div className="lp-wrap lp-cta__inner">
            <div>
              <h2 id="lp-cta-title">{t('landing.cta.title')}</h2>
              <p>{t('landing.cta.body')}</p>
            </div>
            <div className="lp-cta__actions">
              <a href={mailto} className={buttonClass({ variant: 'accent', size: 'lg' })}>
                <Mail size={20} aria-hidden="true" />
                {t('landing.cta.contact')}
              </a>
              <CreateOwnLink signedIn={signedIn} testId="lp-create-cta" />
            </div>
          </div>
        </section>
      </main>

      <footer className="lp-footer">
        <div className="lp-wrap lp-footer__grid">
          <div className="lp-footer__brand">
            <span className="lp-footer__lockup" aria-hidden="true">
              <LogoMark size={28} />
              <Wordmark />
            </span>
            <p>{t('landing.footer.tagline')}</p>
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
          </div>
          <nav aria-label={t('landing.footer.product')}>
            <h3>{t('landing.footer.product')}</h3>
            <a href="#lp-live">{t('landing.footer.live')}</a>
            <a href="#lp-caddy">{t('landing.footer.caddy')}</a>
            <a href="#lp-console">{t('landing.footer.console')}</a>
            {showPricing && <a href="#pricing">{t('landing.nav.pricing')}</a>}
          </nav>
          <nav aria-label={t('landing.footer.access')}>
            <h3>{t('landing.footer.access')}</h3>
            <Link to="/login">{t('landing.nav.login')}</Link>
            <Link to="/register">{t('landing.footer.register')}</Link>
            <Link to="/admin">{t('landing.footer.admin')}</Link>
            <Link to="/me/tournaments">{t('landing.footer.clubs')}</Link>
          </nav>
        </div>
        <div className="lp-wrap lp-footer__legal">
          <span>{t('landing.footer.rights', { year })}</span>
          <span>{t('landing.footer.made')}</span>
        </div>
      </footer>
    </div>
  );
}
